import { default as React } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import PublicIcon from '@mui/icons-material/Public';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import PortIcon from '@mui/icons-material/SettingsEthernet';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';

/**
 * Layout mirrors MicroappConfigCard (internal apps) so public and internal
 * app pages look the same: 180px uppercase label column, inline 14px icon,
 * 0.875rem values, "Metadata" section, right-aligned Delete button.
 */
const useStyles = makeStyles((theme: Theme) => ({
  infoRow: {
    display: 'flex',
    marginBottom: theme.spacing(1.5),
    alignItems: 'flex-start',
  },
  label: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontWeight: 'bold',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    width: '180px',
    flexShrink: 0,
    paddingTop: '2px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  labelIcon: {
    fontSize: '0.875rem',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
  value: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    flex: 1,
    wordBreak: 'break-word',
  },
  chip: {
    height: '20px',
    fontSize: '0.75rem',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  // Same look as UnregisterAppButton on internal app pages.
  deleteButton: {
    marginTop: theme.spacing(2),
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    textTransform: 'none',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

/** Any @mui/icons-material icon (they all share this type). */
type IconComponent = typeof PersonIcon;

/** One label/value row. Label is uppercase with an optional inline icon. */
function InfoRow({
  icon: Icon,
  label,
  children,
}: {
  icon?: IconComponent;
  label: string;
  children: React.ReactNode;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.infoRow}>
      <Typography className={classes.label}>
        {Icon && <Icon className={classes.labelIcon} />}
        {label}
      </Typography>
      <Box className={classes.value}>{children}</Box>
    </Box>
  );
}

const splitCsv = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);

export const PublicAppConfigCard = ({
  isOwner,
  onDeleteClick,
}: {
  isOwner: boolean;
  onDeleteClick: () => void;
}) => {
  const { entity } = useEntity();
  const classes = useStyles();
  const annotations = entity.metadata.annotations ?? {};

  const containerPort = annotations['microapp/port'];
  const hostedAppUrl = annotations['skystage.io/hosted-app-url'];
  const ownerEmails = splitCsv(annotations['microapp/owners']);
  const allowedGroupNames = splitCsv(
    annotations['skystage.io/allowed-group-names'],
  );
  // IDP-1277: persistent storage (same annotations as internal microapps).
  const hasPersistentStorage =
    annotations['microapp/persistentStorage'] === 'true';
  const storageSize = annotations['microapp/storageSize'];

  return (
    <InfoCard title="Application Configuration">
      <Box>
        <InfoRow label="App Type">
          <Chip
            label="Public"
            size="small"
            color="primary"
            icon={<PublicIcon />}
            className={classes.chip}
          />
        </InfoRow>

        <InfoRow icon={VpnLockIcon} label="Requires VPN">
          <Chip
            label="No"
            size="small"
            color="success"
            variant="outlined"
            className={classes.chip}
          />
        </InfoRow>

        {containerPort && (
          <InfoRow icon={PortIcon} label="Container Port">
            <Typography className={classes.value}>{containerPort}</Typography>
          </InfoRow>
        )}

        {hostedAppUrl && (
          <InfoRow icon={LinkIcon} label="Hosted URL">
            <Link
              href={hostedAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: '0.875rem', wordBreak: 'break-all' }}
            >
              {hostedAppUrl}
            </Link>
          </InfoRow>
        )}

        {hasPersistentStorage && (
          <>
            <Divider className={classes.divider} />
            <Typography className={classes.sectionTitle}>
              <StorageIcon className={classes.labelIcon} />
              Persistent Storage
            </Typography>

            <InfoRow label="Status">
              <Chip
                label="Enabled"
                size="small"
                color="primary"
                className={classes.chip}
              />
            </InfoRow>

            {storageSize && (
              <InfoRow icon={StorageIcon} label="Storage Size">
                <Typography className={classes.value}>{storageSize}</Typography>
              </InfoRow>
            )}

            <InfoRow icon={FolderIcon} label="Mount Path">
              <Typography className={classes.value}>/data</Typography>
            </InfoRow>
          </>
        )}

        <Divider className={classes.divider} />
        <Typography className={classes.sectionTitle}>Metadata</Typography>

        <InfoRow icon={PersonIcon} label="Owners">
          <Typography className={classes.value}>
            {ownerEmails.length > 0 ? ownerEmails.join(', ') : 'Unknown'}
          </Typography>
        </InfoRow>

        {allowedGroupNames.length > 0 && (
          <InfoRow icon={GroupsIcon} label="Client Groups">
            <Box className={classes.chipContainer}>
              {allowedGroupNames.map(group => (
                <Chip
                  key={group}
                  label={group}
                  size="small"
                  variant="outlined"
                  className={classes.chip}
                />
              ))}
            </Box>
          </InfoRow>
        )}

        {isOwner && (
          <>
            <Divider className={classes.divider} />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                size="small"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={onDeleteClick}
                className={classes.deleteButton}
              >
                Delete App
              </Button>
            </Box>
          </>
        )}
      </Box>
    </InfoCard>
  );
};
